import React, { useState } from "react";  
import api from "../api";  
import { Link, useNavigate } from "react-router-dom";  
import { motion } from "framer-motion";  
import {  
  AuthShell,  
  FormField,  
  SubmitButton,  
  MessageBanner,  
  EyeIcon,  
} from "./Login"; // reuse shared primitives  
  
function Register() {  
  const navigate = useNavigate();  
  const [form, setForm] = useState({  
    username: "",  
    email: "",  
    password: "",  
    confirmPassword: "",  
  });  
  const [message, setMessage] = useState("");  
  const [messageType, setMessageType] = useState("");  
  const [isLoading, setIsLoading] = useState(false);  
  const [showPassword, setShowPassword] = useState(false);  
  const [showConfirm, setShowConfirm] = useState(false);  
  const [passwordStrength, setPasswordStrength] = useState(0);  
  const [errors, setErrors] = useState({});  
  
  const calculateStrength = (pw) => {  
    let s = 0;  
    if (pw.length >= 8) s++;  
    if (pw.length >= 12) s++;  
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;  
    if (/\d/.test(pw)) s++;  
    if (/[^a-zA-Z0-9]/.test(pw)) s++;  
    setPasswordStrength(s);  
  };  
  
  const handleChange = (e) => {  
    const { name, value } = e.target;  
    setForm({ ...form, [name]: value });  
    if (errors[name]) setErrors({ ...errors, [name]: "" });  
    if (name === "password") calculateStrength(value);  
  };  
  
  const validate = () => {  
    const errs = {};  
    if (!form.username || form.username.length < 3) errs.username = "At least 3 characters";  
    else if (!/^[a-zA-Z0-9_]+$/.test(form.username)) errs.username = "Letters, numbers and underscores only";  
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Enter a valid email";  
    if (!form.password || form.password.length < 8) errs.password = "At least 8 characters";  
    else if (passwordStrength < 3) errs.password = "Add uppercase, numbers, or symbols to strengthen";  
    if (form.password !== form.confirmPassword) errs.confirmPassword = "Passwords don't match";  
    setErrors(errs);  
    return Object.keys(errs).length === 0;  
  };  
  
  const handleRegister = async (e) => {  
    e.preventDefault();  
    if (!validate()) {  
      setMessage("Please fix the highlighted fields");  
      setMessageType("error");  
      return;  
    }  
    setIsLoading(true);  
    setMessage("");  
    try {  
      const res = await api.post("/users/register", {  
        username: form.username,  
        email: form.email,  
        password: form.password,  
      });  
      setMessage(res.data.message || "Account created — check your email to verify.");  
      setMessageType("success");  
      setTimeout(() => navigate("/login", { state: { email: form.email } }), 1600);  
    } catch (err) {  
      setMessageType("error");  
      if (err.response?.data?.detail) setMessage(err.response.data.detail);  
      else if (err.message === "Network Error") setMessage("Can't reach the server. Try again.");  
      else setMessage("Registration failed. Try again.");  
    } finally {  
      setIsLoading(false);  
    }  
  };  
  
  const strengthMeta = () => {  
    if (passwordStrength <= 1) return { label: "Weak", color: "bg-red-500", text: "text-red-400" };  
    if (passwordStrength <= 3) return { label: "Medium", color: "bg-yellow-500", text: "text-yellow-400" };  
    return { label: "Strong", color: "bg-green-500", text: "text-green-400" };  
  };  
  const strength = strengthMeta();  
  
  return (  
    <AuthShell>  
      <motion.div  
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}  
        className="w-full max-w-md"  
      >  
        {/* Header */}  
        <div className="mb-6 text-center">  
          <Link to="/" className="inline-flex items-center gap-2 mb-8">  
            <div className="w-8 h-8 bg-gradient-to-br from-gold to-yellow-600 rounded-lg flex items-center justify-center shadow-lg shadow-gold/20">  
              <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 20 20">  
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />  
              </svg>  
            </div>  
            <span className="text-base font-bold text-white tracking-tight">iTrader</span>  
          </Link>  
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Create your account</h1>  
          <p className="text-sm text-gray-400 mt-1.5">Set up in under a minute</p>  
        </div>  
  
        {/* Card */}  
        <div className="bg-gradient-to-br from-gray-900/60 via-black/60 to-gray-900/60 backdrop-blur-xl border border-gray-800 rounded-2xl p-6 sm:p-7 shadow-2xl">  
          <form onSubmit={handleRegister} className="space-y-4">  
            <FormField  
              label="Username"  
              name="username"  
              value={form.username}  
              onChange={handleChange}  
              placeholder="jane_trader"  
              autoComplete="username"  
              disabled={isLoading}  
              error={errors.username}  
              required  
            />  
  
            <FormField  
              label="Email"  
              name="email"  
              type="email"  
              value={form.email}  
              onChange={handleChange}  
              placeholder="you@example.com"  
              autoComplete="email"  
              disabled={isLoading}  
              error={errors.email}  
              required  
            />  
  
            <div>  
              <FormField  
                label="Password"  
                name="password"  
                type={showPassword ? "text" : "password"}  
                value={form.password}  
                onChange={handleChange}  
                placeholder="Create a strong password"  
                autoComplete="new-password"  
                disabled={isLoading}  
                error={errors.password}  
                required  
                trailing={  
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-500 hover:text-gold transition-colors" tabIndex={-1}>  
                    <EyeIcon shown={showPassword} />  
                  </button>  
                }  
              />  
              {form.password && (  
                <div className="mt-2">  
                  <div className="flex items-center justify-between mb-1">  
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider">Strength</span>  
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${strength.text}`}>{strength.label}</span>  
                  </div>  
                  <div className="h-1 bg-black/40 rounded-full overflow-hidden flex gap-0.5">  
                    {[1, 2, 3, 4, 5].map((i) => (  
                      <div key={i} className={`flex-1 transition-all duration-300 ${i <= passwordStrength ? strength.color : "bg-gray-800"}`} />  
                    ))}  
                  </div>  
                </div>  
              )}  
            </div>  
  
            <FormField  
              label="Confirm password"  
              name="confirmPassword"  
              type={showConfirm ? "text" : "password"}  
              value={form.confirmPassword}  
              onChange={handleChange}  
              placeholder="Re-enter your password"  
              autoComplete="new-password"  
              disabled={isLoading}  
              error={errors.confirmPassword}  
              required  
              trailing={  
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="text-gray-500 hover:text-gold transition-colors" tabIndex={-1}>  
                  <EyeIcon shown={showConfirm} />  
                </button>  
              }  
            />  
  
            <SubmitButton loading={isLoading} label="Create account" loadingLabel="Creating…" />  
  
            <p className="text-center text-xs text-gray-400 pt-1">  
              Already have an account?{" "}  
              <Link to="/login" className="text-gold hover:text-yellow-300 font-semibold transition-colors">  
                Sign in  
              </Link>  
            </p>  
          </form>  
  
          <MessageBanner message={message} type={messageType} />  
        </div>  
  
        {/* Terms */}  
        <p className="text-center text-[11px] text-gray-500 mt-5 px-6">  
          By creating an account you agree to our{" "}  
          <a href="/terms" className="text-gold hover:underline">Terms</a>{" "}  
          and{" "}  
          <a href="/privacy" className="text-gold hover:underline">Privacy Policy</a>.  
        </p>  
      </motion.div>  
    </AuthShell>  
  );  
}  
  
export default Register;  